import { PROTOCOL_CATEGORIES } from '@ems/clinical';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { isBackendConfigured } from '@/lib/env';
import { db, unwrap } from '@/lib/db';
import type { Protocol, ProtocolCategoryRow, ProtocolRevision } from '@/types/db';

export const protocolKeys = {
  effective: (orgId: string | null) => ['protocols', 'effective', orgId ?? 'none'] as const,
  detail: (id: string) => ['protocols', 'detail', id] as const,
  history: (id: string) => ['protocols', 'history', id] as const,
  library: (state: string) => ['protocols', 'library', state] as const,
  pendingAcks: (userId: string | undefined) => ['me', 'pending-acks', userId] as const,
};

/** The organization's effective protocol set (org protocols + inherited library), cached for offline use. */
export function useEffectiveProtocols() {
  const { activeOrgId, user } = useAuth();
  return useQuery({
    queryKey: protocolKeys.effective(activeOrgId),
    enabled: isBackendConfigured && Boolean(user),
    queryFn: async () => unwrap(await db.rpc('effective_protocols', { p_org: activeOrgId })) as Protocol[],
  });
}

export function useProtocol(id: string | undefined) {
  const queryClient = useQueryClient();
  const { activeOrgId, user } = useAuth();
  return useQuery({
    queryKey: protocolKeys.detail(id ?? ''),
    enabled: Boolean(id) && isBackendConfigured && Boolean(user),
    initialData: () => queryClient.getQueryData<Protocol[]>(protocolKeys.effective(activeOrgId))?.find((p) => p.id === id),
    initialDataUpdatedAt: () => queryClient.getQueryState(protocolKeys.effective(activeOrgId))?.dataUpdatedAt,
    queryFn: async () => unwrap(await db.from('protocols').select('*').eq('id', id!).single()) as Protocol,
  });
}

export function useProtocolHistory(id: string | undefined) {
  return useQuery({
    queryKey: protocolKeys.history(id ?? ''),
    enabled: Boolean(id) && isBackendConfigured,
    queryFn: async () =>
      unwrap(
        await db
          .from('protocol_revisions')
          .select('id, published_version, change_summary, reviewed_at, updated_at, status')
          .eq('protocol_id', id!)
          .in('status', ['published', 'superseded'])
          .order('published_version', { ascending: false }),
      ) as Pick<ProtocolRevision, 'id' | 'published_version' | 'change_summary' | 'reviewed_at' | 'updated_at' | 'status'>[],
  });
}

export function useLibraryProtocols(state: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: protocolKeys.library(state ?? 'national'),
    enabled: isBackendConfigured && Boolean(user),
    queryFn: async () => {
      let q = db
        .from('protocols')
        .select('id, org_id, state_code, category_slug, code, title, summary, population, version, effective_date, is_sample, tags, updated_at')
        .is('org_id', null)
        .eq('status', 'published')
        .order('title');
      q = state ? q.eq('state_code', state) : q.is('state_code', null);
      return unwrap(await q) as Protocol[];
    },
  });
}

export function useLibraryStateCounts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['protocols', 'library-state-counts'],
    enabled: isBackendConfigured && Boolean(user),
    queryFn: async () => {
      const rows = unwrap(
        await db.from('protocols').select('state_code').is('org_id', null).eq('status', 'published').not('state_code', 'is', null),
      ) as { state_code: string }[];
      return rows.reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.state_code]: (acc[r.state_code] ?? 0) + 1 }), {});
    },
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['protocols', 'categories'],
    staleTime: 24 * 60 * 60 * 1000,
    initialData: PROTOCOL_CATEGORIES.map((c) => ({ slug: c.slug, name: c.name, icon: c.icon, sort_order: c.sortOrder })),
    initialDataUpdatedAt: 0,
    enabled: isBackendConfigured,
    queryFn: async () => unwrap(await db.from('protocol_categories').select('*').order('sort_order')) as ProtocolCategoryRow[],
  });
}

export function usePendingAcknowledgments() {
  const { user } = useAuth();
  return useQuery({
    queryKey: protocolKeys.pendingAcks(user?.id),
    enabled: Boolean(user),
    queryFn: async () =>
      unwrap(await db.rpc('my_pending_acknowledgments')) as { protocol_id: string; org_id: string; title: string; version: number; published_at: string }[],
  });
}

export function useAcknowledgeProtocol() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (protocolId: string) => unwrap(await db.rpc('acknowledge_protocol', { p_protocol: protocolId })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: protocolKeys.pendingAcks(user?.id) });
      queryClient.invalidateQueries({ queryKey: ['me', 'assignments'] });
    },
  });
}
